'use client'
import NextLink from 'next/link'
import { useLocale } from 'next-intl'
import type { ComponentPropsWithoutRef } from 'react'
import { followedDomains } from '@/lib/do-follow-domains'
import {
  getFeatureLink,
  getFeatureIndexLink,
  isFeaturePage,
} from '@/lib/feature-link-utils'
import type { Locale } from '@/i18n/config'

type SmartLinkProps = ComponentPropsWithoutRef<'a'>

/**
 * 智能链接组件
 * 自动处理feature页面的国际化路由
 * 其他链接保持原有行为
 */
export const SmartLink = ({ href, ...props }: SmartLinkProps) => {
  const locale = useLocale()
  const isInternalLink = href && (href.startsWith('/') || href.startsWith('#'))

  if (isInternalLink) {
    let processedHref = href

    // 检查是否是feature页面链接，如果是则转换为国际化路由
    if (href && isFeaturePage(href)) {
      if (href === '/feature') {
        // feature主页
        processedHref = getFeatureIndexLink(locale as Locale)
      } else {
        // feature子页面，提取slug
        const match = href.match(/^\/feature\/(.+)$/)
        if (match) {
          const slug = match[1]
          processedHref = getFeatureLink(slug, locale as Locale)
        }
      }
    }

    return <NextLink href={processedHref} {...props} />
  }

  // External link logic (保持原有逻辑)
  let rel = 'noopener noreferrer'
  try {
    const url = new URL(href || '')
    if (!followedDomains.includes(url.hostname)) {
      rel += ' nofollow'
    }
  } catch (error) {
    // Handle invalid URLs gracefully
    console.error('Invalid URL for SmartLink:', href, error)
  }

  return <a href={href} target="_blank" rel={rel} {...props} />
}
